// 📁 src/components/Sidebar.jsx
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { user } = useAuth();
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // 👑 Role Checks
  const isAdmin = user?.role === "admin";
  const isFounder = user?.role === "user" || user?.role === "founder";

  // 🧭 Base modules list (UNCHANGED)
  const modules = [
    {
      name: "Dashboard Overview",
      path: "/dashboard",
      icon: "📊",
      description:
        "Get a unified view of all your activities — ideas, market research, MVP progress, and funding.",
    },
    {
      name: "Idea Generation Hub",
      path: "/idea-generation",
      icon: "💡",
      description:
        "Leverage AI-powered brainstorming tools to generate innovative startup ideas.",
    },

    ...(isFounder
      ? [
          {
            name: "Mentor Connect",
            path: "/founder/mentors",
            icon: "🤝",
            description:
              "Discover, book, and manage 1:1 mentorship sessions with industry experts.",
          },
        ]
      : []),

    {
      name: "Market Research Suite",
      path: "/market-research",
      icon: "📈",
      description:
        "Use AI insights to analyze markets, competitors, and customer trends.",
    },
    {
      name: "MVP Development Lab",
      path: "/mvp-planner",
      icon: "🛠️",
      description:
        "Create and prototype your MVP with AI-assisted development guidance.",
    },
    {
      name: "AI Testing & Feedback",
      path: "/ai-testing",
      icon: "🤖",
      description:
        "Harness AI to test your MVP and receive actionable feedback.",
    },
    {
      name: "Business Model Builder",
      path: "/business-model-builder",
      icon: "🧱",
      description:
        "Define and optimize your business model with AI-driven recommendations.",
    },
    {
      name: "Go-to-Market Accelerator",
      path: "/gtm-accelerator",
      icon: "🚀",
      description:
        "Plan your launch strategy and campaigns powered by AI insights.",
    },
    {
      name: "Pitch Deck Generator",
      path: "/pitch-deck",
      icon: "🖼️",
      description:
        "Generate compelling AI-crafted pitch decks to attract investors.",
    },
    {
      name: "Funding & Investor Match",
      path: "/funding-match",
      icon: "💰",
      description:
        "Discover funding opportunities with AI-driven investor matching.",
    },
    {
      name: "Learning & Mentorship Hub",
      path: "/learning-mentorship",
      icon: "📚",
      description:
        "Access AI-curated resources and connect with expert mentors.",
    },
    {
      name: "Collaboration & Networking Hub",
      path: "/community-progress",
      icon: "🌐",
      description:
        "Collaborate and network with startups through AI-powered community tools.",
    },
  ];

  // 🎯 Tooltip portal (UNCHANGED)
  const TooltipPortal = ({ text, x, y }) =>
    createPortal(
      <AnimatePresence>
        {text && (
          <motion.div
            key="tooltip"
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="fixed text-[13px] text-white rounded-xl py-2 px-3 shadow-2xl border border-blue-400/40 z-[99999] max-w-xs pointer-events-none
                       bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 backdrop-blur-md"
            style={{
              top: y,
              left: x,
              transform: "translate(12px, -50%)",
              whiteSpace: "pre-wrap",
            }}
          >
            {text}
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    );

  return (
    <motion.aside
      animate={{ width: collapsed ? 80 : 260 }}
      transition={{ duration: 0.25, ease: "easeInOut" }}
      className="h-screen bg-white text-blue-900 border-r shadow-lg fixed top-0 left-0 z-[999] overflow-y-auto flex flex-col"
    >
      {/* Header + Toggle */}
      <div className="flex items-center justify-between px-4 py-5 border-b">
        {!collapsed && (
          <h2 className="text-xl font-extrabold text-blue-800 whitespace-nowrap">
            🚀 Startup Navigator
          </h2>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 rounded-lg hover:bg-blue-50 transition"
        >
          {collapsed ? "➡️" : "⬅️"}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 mt-4 pb-6">
        <ul className="space-y-2">

          {/* 👑 Admin Dashboard */}
          {isAdmin && (
            <>
              <li>
                <NavLink
                  to="/admin-dashboard"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg ${
                      isActive
                        ? "bg-blue-100 text-blue-700"
                        : "hover:bg-blue-50"
                    }`
                  }
                >
                  <span className="text-lg">👑</span>
                  {!collapsed && <span className="font-semibold">Admin Dashboard</span>}
                </NavLink>
              </li>

              {/* ⭐ NEW — Pricing Management */}
              <li>
                <NavLink
                  to="/admin/pricing"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg ${
                      isActive
                        ? "bg-blue-100 text-blue-700"
                        : "hover:bg-blue-50"
                    }`
                  }
                >
                  <span className="text-lg">💰</span>
                  {!collapsed && (
                    <span className="font-semibold">Pricing Management</span>
                  )}
                </NavLink>
              </li>
            </>
          )}

          {/* Dynamic module list (UNCHANGED) */}
          {modules.map(({ name, path, icon, description }, i) => (
            <li
              key={i}
              onMouseEnter={(e) => {
                if (!collapsed) return;
                const rect = e.currentTarget.getBoundingClientRect();
                setHovered(description);
                setTooltipPos({
                  x: rect.right + 10,
                  y: rect.top + rect.height / 2,
                });
              }}
              onMouseLeave={() => setHovered(null)}
            >
              <NavLink
                to={path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg ${
                    isActive
                      ? "bg-blue-100 text-blue-700"
                      : "hover:bg-blue-50"
                  }`
                }
              >
                <span className="text-lg">{icon}</span>
                {!collapsed && <span className="font-semibold">{name}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

        {hovered && (
        <TooltipPortal text={hovered} x={tooltipPos.x} y={tooltipPos.y} />
      )}
    </motion.aside>
  );
};

export default Sidebar;
